import { useCallback, useEffect, useRef, useState } from 'react'
import type { ProjectMessage } from '../transport/api'
import { getMessages, Chatv1Error } from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open, sendChatv1, connectChatv1 } from '../chatv1/ws'
import { chatv1SelfEmail } from '../chatv1/auth'
import { wireToProjectMessage, threadFields } from './projectDynamo'
import { onProjectEcho } from './projectEcho'

/**
 * Live project messages, over the managed chat service.
 *
 * ONE BACKEND, since 2026-09-02. There were two: this and an edge twin that spoke SSE to
 * `/project/events` and forward-paged `/project/history?after=`. Which one the whole app
 * used was decided by a ternary evaluated AT MODULE IMPORT, reading localStorage — so the
 * choice was frozen by whichever value happened to be there when the module first loaded,
 * neither twin could be tested against the other in one process, and 52 call sites across
 * 11 files each carried their own copy of the question. The owner retired the edge lane
 * (Project Chat 舊的路徑完全刪了) and it is gone: no flag, no twin, no branch.
 *
 * The §6.2 reconciliation posture is unchanged, and its mechanics follow this transport:
 * the only forward primitive is the WebSocket `backfill` action, and history pages
 * BACKWARD (`getMessages(before_seq)`). So: load the newest page on open, then reconcile
 * by asking the socket to `backfill` everything after the highest seq we hold — on mount,
 * on `sync`, and on every (re)connect (onChatv1Open), because a dropped frame never
 * arrives late.
 *
 * THE ONE THING NOT TO SIMPLIFY is `merge`. It must stay a PURE updater: React re-invokes
 * updaters, always under StrictMode (main.tsx renders inside it) and whenever it
 * reprocesses the update queue. An earlier version of the edge twin deduped against a ref
 * it MUTATED INSIDE setMessages; the second invocation found every id already seen,
 * skipped all of them, and returned an empty array — which is the result React keeps. The
 * fetch had succeeded, the cursor had advanced, no error was raised, and the transcript
 * just went blank at random. That hook is deleted; the shape is still here, so the
 * StrictMode test came with it.
 *
 * Optimistic sends land as a PENDING row (via App → projectEcho, carrying the client
 * `msg_id`); the server's `ack` for that id settles it to its real seq, and any later
 * `message`/`backfill` echo of the same id is a no-op, not a double.
 */
const PAGE = 50
/** Going back to one message: the most the service gives in a page. */
const REACH_PAGE = 100

export function useProjectMessages(projectId: string | null) {
  const [messages, setMessages] = useState<ProjectMessage[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  /** Bumped on every project change; async work carries the value it started with. */
  const epoch = useRef(0)
  /** Highest REAL seq held — the floor we backfill from. Pending rows never raise it. */
  const highWater = useRef(0)
  /**
   * Lowest REAL seq held — where the next OLDER page starts (`before_seq`). Owner,
   * 2026-10-02: a project begun at 9:00 showed nothing before 15:16 — only the newest page
   * was ever fetched, and scrolling to the top asked for nothing more.
   */
  const lowWater = useRef(0)
  /** Whether the server may hold messages older than lowWater. */
  const [hasOlder, setHasOlder] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const olderInFlight = useRef(false)
  /** The older page on its way, for a jump to wait on rather than ask twice (`reach`). */
  const olderWait = useRef<Promise<unknown> | null>(null)

  /**
   * De-duplicating insert with pending-reconcile. Pure updater (no refs mutated inside
   * setMessages — StrictMode double-invokes it): "already have it" is derived from `prev`.
   * A real row REPLACES a pending row of the same id (our own send, now confirmed).
   */
  const merge = useCallback((incoming: ProjectMessage[]) => {
    if (incoming.length === 0) return
    for (const m of incoming) {
      if (m.status !== 'pending' && typeof m.cursor === 'number' && m.cursor > highWater.current) {
        highWater.current = m.cursor
      }
      if (m.status !== 'pending' && typeof m.cursor === 'number' && m.cursor > 0 && (lowWater.current === 0 || m.cursor < lowWater.current)) {
        lowWater.current = m.cursor
      }
    }
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.message_id, m]))
      let changed = false
      for (const m of incoming) {
        const existing = byId.get(m.message_id)
        if (!existing) {
          byId.set(m.message_id, m)
          changed = true
        } else if (existing.status === 'pending' && m.status !== 'pending') {
          byId.set(m.message_id, m)
          changed = true
        }
      }
      if (!changed) return prev
      const next = [...byId.values()]
      // Display order is (ts, then cursor) — the pending row's max-cursor keeps it last
      // until its real seq lands.
      next.sort((a, b) => a.ts - b.ts || a.cursor - b.cursor)
      return next
    })
  }, [])

  /** Reconcile: ask the socket for everything after our high-water. Safe any time. */
  const sync = useCallback(() => {
    if (!projectId) return
    connectChatv1()
    sendChatv1({ action: 'backfill', conv_id: projectId, since_seq: highWater.current })
  }, [projectId])

  // Reset and load the newest page whenever the open project changes.
  useEffect(() => {
    epoch.current++
    const mine = epoch.current
    highWater.current = 0
    lowWater.current = 0
    olderInFlight.current = false
    setHasOlder(false)
    setLoadingOlder(false)
    setMessages([])
    setError(null)
    if (!projectId) return
    setLoading(true)
    connectChatv1()
    const selfEmail = chatv1SelfEmail() || null
    getMessages(projectId, undefined, PAGE)
      .then((r) => {
        if (epoch.current !== mine) return
        merge(r.messages.map((m) => wireToProjectMessage(m, selfEmail)))
        setHasOlder(r.messages.length >= PAGE)
        setError(null)
        setLoading(false)
      })
      .catch((e) => {
        if (epoch.current !== mine) return
        // Surface the backend's own code (`no_token`, `device_not_registered`, …) rather
        // than the class-name-prefixed toString, which reads as noise on the panel.
        setError(e instanceof Chatv1Error ? e.code : String(e))
        setLoading(false)
      })
  }, [projectId, merge])

  // Live frames: message/backfill merge; ack settles our pending; error fails it.
  useEffect(() => {
    if (!projectId) return
    const selfEmail = chatv1SelfEmail() || null
    return onChatv1Frame((f) => {
      if (f.type === 'message' && f.conv_id === projectId) {
        merge([wireToProjectMessage(f, selfEmail)])
      } else if (f.type === 'backfill' && f.conv_id === projectId) {
        merge(f.messages.map((m) => wireToProjectMessage(m, selfEmail)))
      } else if (f.type === 'ack' && f.conv_id === projectId) {
        if (f.seq > highWater.current) highWater.current = f.seq
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.message_id === f.msg_id)
          if (i < 0) return prev
          const next = [...prev]
          // A reply to a reply lands under that one's top: the server says which (one level).
          next[i] = { ...next[i], cursor: f.seq, ts: f.ts, status: 'sent', ...(typeof f.reply_to === 'number' ? threadFields(f) : {}) }
          next.sort((a, b) => a.ts - b.ts || a.cursor - b.cursor)
          return next
        })
      } else if (f.type === 'error' && f.msg_id) {
        setMessages((prev) => {
          const i = prev.findIndex((m) => m.message_id === f.msg_id)
          if (i < 0) return prev
          const next = [...prev]
          next[i] = { ...next[i], status: 'failed', error: f.code }
          return next
        })
      }
    })
  }, [projectId, merge])

  // Our own send, shown instantly — the composer (App) emits the PENDING row here the
  // moment it fires, so the sender sees it before any round trip. The server never
  // pushes a `message` echo back to the originating socket (fanout skips it), so
  // without this the sender's own line would not appear until a later reconcile. The
  // `ack` frame (same msg_id) settles this pending row to its real seq; a later
  // history/backfill copy of the id is de-duped by merge, not doubled.
  useEffect(() => {
    if (!projectId) return
    return onProjectEcho(projectId, (msg) => merge([msg as ProjectMessage]))
  }, [projectId, merge])

  // Reconcile on every (re)connect — a dropped frame only ever fails to arrive (§4.3).
  useEffect(() => {
    if (!projectId) return
    return onChatv1Open(() => sync())
  }, [projectId, sync])

  /** The page before the oldest message held. Safe to call repeatedly; one at a time. */
  const loadOlder = useCallback(() => {
    if (!projectId || olderInFlight.current || lowWater.current <= 1) return
    olderInFlight.current = true
    setLoadingOlder(true)
    const mine = epoch.current
    const selfEmail = chatv1SelfEmail() || null
    olderWait.current = getMessages(projectId, lowWater.current, PAGE)
      .then((r) => {
        if (epoch.current !== mine) return
        merge(r.messages.map((m) => wireToProjectMessage(m, selfEmail)))
        setHasOlder(r.messages.length >= PAGE && lowWater.current > 1)
      })
      .catch(() => {
        // Left as it was: the next scroll to the top asks again.
      })
      .finally(() => {
        if (epoch.current !== mine) return
        olderInFlight.current = false
        setLoadingOlder(false)
      })
  }, [projectId, merge])

  /**
   * Load back to `seq` — a bookmark's message (chat/useRoomBookmarks): older pages, the largest the service gives, until
   * the oldest held is at or before it or there is nothing older. True when it is now within what is held (it may still
   * be absent: removed since). Waits for a page already on its way rather than asking twice.
   */
  const reach = useCallback(
    async (seq: number): Promise<boolean> => {
      if (!projectId) return false
      const mine = epoch.current
      if (olderInFlight.current) await olderWait.current?.catch(() => {})
      if (epoch.current !== mine || olderInFlight.current) return false
      if (lowWater.current > 0 && lowWater.current <= seq) return true
      olderInFlight.current = true
      setLoadingOlder(true)
      const selfEmail = chatv1SelfEmail() || null
      const going = (async () => {
        try {
          while (lowWater.current > seq && lowWater.current > 1) {
            const r = await getMessages(projectId, lowWater.current, REACH_PAGE)
            if (epoch.current !== mine) return false
            merge(r.messages.map((m) => wireToProjectMessage(m, selfEmail)))
            const more = r.messages.length >= REACH_PAGE && lowWater.current > 1
            setHasOlder(more)
            if (!more) break
          }
          return lowWater.current > 0 && lowWater.current <= seq
        } catch {
          return false
        } finally {
          if (epoch.current === mine) {
            olderInFlight.current = false
            setLoadingOlder(false)
          }
        }
      })()
      olderWait.current = going
      return going
    },
    [projectId, merge],
  )

  return { messages, error, loading, sync, hasOlder, loadingOlder, loadOlder, reach }
}
